// Het beeld bij "Waarom vragen we dit?" (epic 12) — een echt dier uit het aanbod, geen stockfoto.
//
// De mockup zet naast elke stap een foto. Die foto's zijn er niet als bestand, en vijf losse
// stockplaatjes bijleveren zou het vreemdste beeld opleveren dat deze site kan tonen: een
// matchwizard voor asieldieren, geïllustreerd met dieren die niet bestaan. Het aanbod zelf staat
// al klaar en heeft foto's, dus die gebruiken we.
//
// Er zit nog een tweede winst in. Na stap 1 weten we welke diersoort de bezoeker zoekt, dus het
// beeld kan meebewegen: wie katten zoekt, krijgt katten naast zijn vragen te zien. Het is
// versiering, geen gegeven waar iets van afhangt — mislukt de aanroep, dan valt het blok terug op
// het icoon en gaat de wizard gewoon door.

import { useEffect, useState } from "react";

import { fetchSearch, type PublicAnimalSummary } from "../publicApi";

/** Eén dier zoals het blok het toont: de foto plus wie erop staat. */
export interface WaaromFoto {
  url: string;
  alt: string;
  naam: string;
  opvangNaam: string | null;
}

/** Hoeveel dieren we per soort ophalen. Genoeg voor vijf stappen zonder herhaling. */
const OMVANG = 18;

/**
 * Wat er al opgehaald is, per groep. Module-breed, zodat heen en weer stappen door de wizard —
 * en terugkomen na de samenvatting — geen tweede aanroep kost.
 */
const GEHEUGEN = new Map<string, WaaromFoto[]>();

/** De groepen waar de zoekvraag een `groep`-filter voor kent; de rest zoekt ongefilterd. */
const GROEPEN = new Set(["honden", "katten", "konijnen-en-knagers", "vogels"]);

/** De sleutel waaronder een keuze in het geheugen zit; "" is het hele aanbod. */
export function groepVan(soort: readonly string[] | undefined): string {
  const keuze = soort?.[0];
  return keuze && GROEPEN.has(keuze) ? keuze : "";
}

/**
 * Namen waar een testrecord zich mee verraadt. Op acc staat "TESTDIER NIET ECHT" met een blauw
 * vlak als foto, en dat is precies het dier dat het blok als eerste zou pakken.
 *
 * <p>Een naamregel is een slappe regel, en daarom staat hij alleen hier: dit blok is versiering.
 * De zoekpagina laat zo'n dier gewoon zien, want wat in het aanbod staat hoort in het aanbod te
 * staan. Wil je zo'n record echt weg, dan hoort dat aan de bron te gebeuren — de advertentie
 * intrekken in de backoffice die hem publiceerde.
 */
const TESTNAAM = /\btest(dier)?\b|niet echt|dummy|voorbeeld/i;

function alsFoto(dier: PublicAnimalSummary): WaaromFoto | null {
  if (!dier.hoofdfotoUrl) return null;
  if (TESTNAAM.test(dier.naam)) return null;
  return {
    url: dier.hoofdfotoUrl,
    alt: dier.hoofdfotoAlt ?? `${dier.naam}, ${dier.soort ?? "dier"} in de opvang`,
    naam: dier.naam,
    opvangNaam: dier.opvangNaam,
  };
}

/**
 * De foto's bij een soortkeuze. Leeg zolang er niets opgehaald is en leeg als het ophalen
 * mislukt — het blok hoort daar niet anders van te worden dan iets stiller.
 */
export function useWaaromFotos(soort: readonly string[] | undefined): WaaromFoto[] {
  const groep = groepVan(soort);
  const [fotos, setFotos] = useState<WaaromFoto[]>(() => GEHEUGEN.get(groep) ?? []);

  useEffect(() => {
    const gehad = GEHEUGEN.get(groep);
    if (gehad) {
      setFotos(gehad);
      return;
    }
    let levend = true;
    fetchSearch({
      kanaal: "DIERMATCH",
      alleenBeschikbaar: true,
      size: OMVANG,
      ...(groep ? { groep } : {}),
    })
      .then((r) => {
        const uit = r.items.map(alsFoto).filter((f): f is WaaromFoto => f !== null);
        GEHEUGEN.set(groep, uit);
        if (levend) setFotos(uit);
      })
      .catch(() => {
        // Versiering: stil falen, het blok toont dan zijn icoon.
        if (levend) setFotos([]);
      });
    return () => {
      levend = false;
    };
  }, [groep]);

  return fotos;
}

/**
 * Welke foto bij welke stap hoort. Op positie, niet op toeval: dezelfde stap toont hetzelfde dier
 * zolang de soortkeuze gelijk blijft, zodat terugbladeren geen nieuw plaatje oplevert.
 */
export function fotoVoor(fotos: readonly WaaromFoto[], positie: number): WaaromFoto | null {
  if (fotos.length === 0) return null;
  return fotos[positie % fotos.length];
}

/** Alleen voor tests: het geheugen leegmaken zodat elke test zijn eigen aanroep ziet. */
export function vergeetWaaromFotos(): void {
  GEHEUGEN.clear();
}
